import { events } from "@constants/tracking";
import { ConfirmDialog } from "@memorang/ui/components/ConfirmDialog";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import { trackCustomEvent } from "analytics";
import { memo, useEffect } from "react";
import { Button } from "react-native-paper";
import {
	type DialogType,
	getDialogSessionConfigs,
} from "../constants/dialog-configs";

type Props = {
	open: boolean;
	handleClose?: () => void;
	type?: DialogType;
	handleClickButton?: (type: string) => void;
	customConfig?: {
		title: string;
		content: string;
		buttons?: {
			label: string;
			type: "cancel" | "confirm";
		}[];
		icon: string;
	};
	maxWidth?: unknown;
	showCloseButton?: boolean;
	confirmLoading?: boolean;
	subtitleComponent?: React.ReactNode;
	currentSection?: number;
	numTotalSections?: number;
	currentPart?: number;
	numTotalParts?: number;
	nextPartName?: string;
	timeRemainingSeconds?: number;
	numItemsLeft?: number;
	itemTimeLimitSeconds?: number;
	isLastQuestion?: boolean;
	paragraphMarginVertical?: number;
};

const DialogSession = memo(
	function DialogSession({
		open,
		handleClose,
		type,
		handleClickButton,
		customConfig,
		maxWidth,
		showCloseButton = true,
		confirmLoading,
		subtitleComponent,
		currentSection,
		numTotalSections,
		currentPart,
		numTotalParts,
		nextPartName,
		timeRemainingSeconds,
		numItemsLeft,
		itemTimeLimitSeconds,
		isLastQuestion,
		paragraphMarginVertical,
	}: Props) {
		const theme = useAppTheme();

		useEffect(() => {
			if (open) {
				trackCustomEvent({
					eventName: events.dialogOpened,
					type,
				});
			}
		}, [open, type]);

		const config =
			customConfig ||
			(type
				? getDialogSessionConfigs({
						currentSection,
						numTotalSections,
						currentPart,
						numTotalParts,
						nextPartName,
						timeRemainingSeconds,
						numItemsLeft,
						itemTimeLimitSeconds,
						isLastQuestion,
					})[type]
				: undefined);

		if (!config) {
			return null;
		}

		const { title, content, buttons, icon } = config;

		const colorMap: Record<string, string> = {
			cancel: theme.colors.onSurfaceVariant,
			confirm: theme.colors.primary,
		};

		const canDismiss = type !== "itemTimeExpired";

		const handleDialogClose = () => {
			handleClose?.();
			trackCustomEvent({
				eventName: events.dialogClosed,
				type,
			});
		};

		const renderActions = () =>
			handleClickButton && buttons
				? buttons.map(({ label, type }) => (
						<Button
							key={label}
							onPress={() => {
								handleClickButton(type);
							}}
							textColor={colorMap[type]}
							loading={confirmLoading && type === "confirm"}
							disabled={confirmLoading}
						>
							{label}
						</Button>
					))
				: null;

		return (
			<ConfirmDialog
				visible={open}
				dismissCallback={canDismiss ? handleDialogClose : undefined}
				dismissable={canDismiss}
				body={content}
				showCloseButton={canDismiss && showCloseButton}
				title={title}
				subtitleComponent={subtitleComponent}
				maxWidth={maxWidth}
				icon={icon}
				actions={renderActions()}
				paragraphMarginVertical={paragraphMarginVertical}
			/>
		);
	},
	(prevProps, nextProps) => {
		return (
			prevProps.open === nextProps.open &&
			prevProps.type === nextProps.type &&
			prevProps.confirmLoading === nextProps.confirmLoading &&
			prevProps.currentSection === nextProps.currentSection &&
			prevProps.numTotalSections === nextProps.numTotalSections &&
			prevProps.currentPart === nextProps.currentPart &&
			prevProps.numTotalParts === nextProps.numTotalParts &&
			prevProps.nextPartName === nextProps.nextPartName &&
			prevProps.timeRemainingSeconds === nextProps.timeRemainingSeconds &&
			prevProps.numItemsLeft === nextProps.numItemsLeft &&
			prevProps.itemTimeLimitSeconds === nextProps.itemTimeLimitSeconds &&
			prevProps.isLastQuestion === nextProps.isLastQuestion &&
			prevProps.customConfig === nextProps.customConfig &&
			prevProps.subtitleComponent === nextProps.subtitleComponent &&
			prevProps.handleClose === nextProps.handleClose &&
			prevProps.handleClickButton === nextProps.handleClickButton
		);
	},
);

export default DialogSession;
